// src/components/DetailSidebarNews.jsx
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ExternalLink } from "lucide-react";
import { redirectToLogin } from "../utils/auth";

export default function DetailSidebarNews({
  open,
  onClose,
  newsId,
  jwtToken,
  apiUrl = "https://whiteintel.io/api/regular/app/v2/newsfeed/get_news.php",
  backdropZ = 400,
  panelZ = 500,
}) {
  const canOpen = open && Boolean(newsId) && Boolean(jwtToken);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [post, setPost] = useState(null);
  const abortRef = useRef(null);

  // External link modal
  const [externalLink, setExternalLink] = useState(null);

  // Body scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [open]);

  // ESC close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setPost(null);
      setErr("");
      setExternalLink(null);
    }
  }, [open]);

  // Fetch detail
  useEffect(() => {
    if (!canOpen) return;

    setLoading(true);
    setErr("");

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      try {
        const res = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify({ metric: "detail", id: newsId }),
          signal: controller.signal,
        });

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const raw = await res.json();
        setPost(raw?.data ?? raw ?? null);

      } catch (e) {
        if (e.name !== "AbortError") setErr(e.message || "Failed to load news");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [canOpen, apiUrl, jwtToken, newsId]);

  const meta = post || {};

  // Format date
  const fmtDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? "—" : dt.toISOString().slice(0, 10);
  };

  // ---- Parse AI Summary JSON ----
  const ai = (() => {
    try {
      return meta.ai_summary ? JSON.parse(meta.ai_summary) : null;
    } catch {
      return null;
    }
  })();

  return (
    <>
      {/* Backdrop */}
      {open &&
        createPortal(
          <div
            role="presentation"
            onClick={onClose}
            aria-hidden="true"
            style={{ zIndex: backdropZ }}
            className="fixed inset-0 bg-black/60 transition-opacity opacity-100"
          />,
          document.body
        )}

      {/* Panel */}
      {open &&
        createPortal(
          <aside
            role="dialog"
            aria-modal="true"
            style={{ zIndex: panelZ }}
            className={`fixed right-0 top-0 h-full w-full max-w-2xl
                        border-l border-sky-900/40
                        bg-[#06101e]
                        shadow-[0_0_50px_rgba(0,0,0,0.5)]
                        transform transition-transform duration-300 translate-x-0`}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-sky-900/30 bg-[#0a1224]/60">
              <div className="min-w-0">
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-sky-300/70 font-semibold mb-2">Public News</div>
                <h2 className="text-[18px] font-semibold text-white leading-snug">
                  {meta.title || "News Article"}
                </h2>
              </div>

              <button
                title="Close"
                onClick={onClose}
                className="shrink-0 rounded-md p-1.5 text-neutral-500 hover:text-white hover:bg-white/[0.06] transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto p-5 pb-24 h-[calc(100%-80px)]">
              {loading ? (
                <Loader />
              ) : err ? (
                <div className="text-red-400 text-sm">{err}</div>
              ) : (
                post && (
                  <div className="space-y-8">
                    {/* Metadata */}
                    <Section title="Metadata">
                      <MetaRow label="Source" value={meta.source} />
                      <MetaRow label="Published" value={fmtDate(meta.published_at)} />
                      <MetaRow
                    label="Tags"
                    value={Array.isArray(meta.tags) ? meta.tags.join(", ") : "—"}
                    />

                    </Section>

                    {/* ---------------- AI SUMMARY (if exists) ---------------- */}
                    {ai ? (
                      <Section title="AI Summary">
                        {ai.overview && (
                          <div className="text-sm text-neutral-200 whitespace-pre-wrap leading-relaxed mb-4">
                            {ai.overview}
                          </div>
                        )}

                        {Array.isArray(ai.key_points) && ai.key_points.length > 0 && (
                          <ul className="list-disc ml-5 space-y-1 text-sm text-neutral-200">
                            {ai.key_points.map((p, i) => (
                              <li key={i}>{p}</li>
                            ))}
                          </ul>
                        )}

                        {ai.why_it_matters && (
                          <div className="mt-4 text-sm text-neutral-300 italic">
                            {ai.why_it_matters}
                          </div>
                        )}

                            {/* ADD THIS */}
    {meta.link && (
      <button
        className="mt-5 px-3.5 py-2 rounded-md border border-sky-600/40 bg-sky-500/15 text-sky-100 hover:bg-sky-500/25 flex items-center gap-2 text-sm font-medium transition-colors"
        onClick={() => setExternalLink(meta.link)}
      >
        <ExternalLink className="h-4 w-4" />
        Read full article
      </button>
    )}
                      </Section>
                    ) : (
                      /* -------- If NO AI summary → show normal summary -------- */
                      meta.summary && (
                        <Section title="Summary">
                          <div className="text-sm text-neutral-200 whitespace-pre-wrap leading-relaxed mb-4">
                            {meta.summary}
                          </div>

                          {meta.link && (
                            <button
                              className="px-3.5 py-2 rounded-md border border-sky-600/40 bg-sky-500/15 text-sky-100 hover:bg-sky-500/25 flex items-center gap-2 text-sm font-medium transition-colors"
                              onClick={() => setExternalLink(meta.link)}
                            >
                              <ExternalLink className="h-4 w-4" />
                              Read full article
                            </button>
                          )}
                        </Section>
                      )
                    )}
                  </div>
                )
              )}
            </div>
          </aside>,
          document.body
        )}

      {/* External Link Modal */}
      <ExternalLinkModal
        url={externalLink}
        onCancel={() => setExternalLink(null)}
        onConfirm={() => {
          if (externalLink) {
            window.open(externalLink, "_blank", "noopener");
          }
          setExternalLink(null);
        }}
      />
    </>
  );
}

/* ---------------- Components ---------------- */

function Section({ title, children }) {
  return (
    <section className="mt-4 first:mt-0">
      <h3 className="mb-3 text-[11px] font-semibold tracking-wider text-neutral-300 uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="mb-2">
      <div className="text-[11px] uppercase tracking-wide text-neutral-500">
        {label}
      </div>
      <div className="text-sm text-neutral-100">{value || "—"}</div>
    </div>
  );
}

function Loader() {
  return (
    <div className="p-5 space-y-5">
      <div className="h-5 w-36 bg-neutral-800/60 animate-pulse rounded" />
      <div className="h-4 w-64 bg-neutral-800/60 animate-pulse rounded" />
      <div className="h-4 w-48 bg-neutral-800/60 animate-pulse rounded" />
    </div>
  );
}

/* ---------------- External Link Modal ---------------- */

function ExternalLinkModal({ url, onCancel, onConfirm }) {
  if (!url) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60"
      onClick={onCancel}
    >
      <div
        className="rounded-md border border-white/10 bg-[#000414] p-6 w-[90%] max-w-sm shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[15px] font-semibold mb-2">Open external link?</div>

        <div className="text-sm text-neutral-300 break-all mb-5">{url}</div>

        <div className="flex justify-end gap-3">
          <button
            className="px-3 py-1.5 rounded-md border border-white/20 text-neutral-300 hover:bg-white/5 transition-colors"
            onClick={onCancel}
          >
            Cancel
          </button>

          <button
            className="px-3 py-1.5 rounded-md border border-sky-500/40 bg-sky-500/15 text-sky-100 hover:bg-sky-500/25 transition-colors"
            onClick={onConfirm}
          >
            Open
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
